/**
 * The maths behind the logo crop dialog, and the canvas call that ends it.
 *
 * Everything here except `cropImage` is pure and synchronous, which is the point of the
 * split: pan, zoom and resize are geometry with a lot of clamping to get right, and geometry
 * can be tested without a DOM, a canvas or an image that jsdom will never decode. The dialog
 * holds a `CropState` and swaps it for a new one on every pointer move; only Save touches a
 * canvas.
 *
 * No cropping library, deliberately. TypeScript is pinned to ^4.9.5 by CRA 5 -- the same
 * constraint that pins @mui/x-charts to 8.16.0, whose newer typings 4.9 cannot even parse --
 * and Jest 27 predates the `exports` field, which is why package.json already carries five
 * `moduleNameMapper` entries. A cropper would likely need a sixth and a TypeScript upgrade.
 * This is a hundred lines of arithmetic with no runtime dependency.
 *
 * **The crop may select area the image does not cover**, and that is a feature rather than an
 * oversight. A mark exported flush to its own edges cannot be given breathing room by a crop
 * that can only ever take away, and a wide wordmark cannot be padded out to a square. So the
 * frame is bounded by the stage, the zoom goes below fit, and whatever the image does not
 * reach is left to the encoding: transparent under PNG, and the white a JPEG has to fill
 * with because it has no alpha to leave alone.
 */

import { loadImage } from './imageHelper';

export interface Size {
    width: number;
    height: number;
}

export interface Rect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export type Corner = 'nw' | 'ne' | 'se' | 'sw';

/** Everything the crop maths needs, so no helper has to take five arguments. */
export interface CropState {
    /** The source image's own pixels. */
    natural: Size;
    /** The stage box, in CSS px. A constant the dialog also styles with -- never measured. */
    stage: Size;
    /**
     * 1 fits the whole image inside the stage. Above that the image overflows the stage and
     * the crop is a detail of it; below that the image is smaller than the frame may be, and
     * the difference is the transparent margin the admin is buying.
     */
    zoom: number;
    /** Where the drawn image's top-left sits, in stage px. Goes negative when zoomed in. */
    offset: { x: number; y: number };
    /** The crop rectangle, in stage px. Free aspect unless `aspect` says otherwise. */
    frame: Rect;
    /**
     * Width divided by height, when the frame is locked to a shape.
     *
     * Absent for the logo, the watermark and the hero, all of which are framed freely so a
     * wordmark can be the wide strip it is. Present, and 1, for an avatar: it is drawn inside
     * a circle everywhere it appears, and a non-square crop put through a circular mask is
     * cropped a second time by CSS -- off-centre, and in a way the dialog's preview would
     * have to lie about. Locking the frame is what makes the preview honest.
     */
    aspect?: number;
}

/**
 * The long edge of what gets uploaded.
 *
 * A header logo is drawn at most 38 CSS px tall and the admin's own preview box is 204x60,
 * so 512 is already generous at 2x. It is also what sizes the server's cap: a 512px square
 * of incompressible RGBA is ~1.05MB as PNG, which is why that cap is 2MB and not 1MB.
 */
export const MAX_LOGO_EDGE = 512;

/**
 * The long edge of a hero photograph, which is a different kind of picture entirely.
 *
 * A logo is drawn at 38px and bounded at 512; a hero fills the width of the page behind the
 * headline, so it is bounded at the width of a laptop screen instead. 1920 is where the
 * returns stop: above it the file grows with the square of the edge and the difference is
 * invisible on every display that is not being used to inspect the photograph itself.
 *
 * It is also what sizes the server's 4MB cap for this route -- see `BrandImage.MAX_PHOTO_BYTES`.
 * Raising this means raising that in the same change.
 */
export const MAX_HERO_EDGE = 1920;

/**
 * The long edge of a profile picture, which is square, so it is both edges.
 *
 * The same 512 the logo uses, and by coincidence rather than by sharing: a logo is bounded
 * there because it is drawn at 38px, an avatar because the largest one the app draws is the
 * 88px circle on the profile page. 512 is ~3x that, which covers a 3x display and a future
 * larger avatar without carrying a photograph's worth of pixels into every patient list.
 */
export const MAX_AVATAR_EDGE = 512;

/**
 * The edge of a favicon, which is square, so it is both edges.
 *
 * Smaller than everything above it because a browser tab draws this at 16 CSS px and a
 * bookmark bar or a pinned tab at 32. 256 is 8x the largest of those, which covers a 3x
 * display with room to spare and is where the returns stop: nothing renders a favicon larger,
 * so the extra pixels of a 512 would be carried into every page load and drawn by nobody.
 *
 * Well under the server's 2MB cap either way -- a 256px square of incompressible RGBA is
 * ~260KB as PNG, a quarter of what the logo's 512 can reach.
 */
export const MAX_FAVICON_EDGE = 256;

/**
 * A quarter of fit, so the frame can reach about four times the mark's long edge.
 *
 * Enough to square off a 4:1 wordmark, which is the widest shape this has to serve. It is a
 * floor rather than a preference: every pixel of margin is a pixel the encoding's `maxEdge`
 * then spends on empty space, so an admin who zooms all the way out is trading resolution for
 * padding and should not be able to trade away much more of it than this.
 */
export const MIN_ZOOM = 0.25;

export const MAX_ZOOM = 8;

/** Below this the frame is too small to grab a handle on, let alone aim. */
export const MIN_FRAME_PX = 32;

/**
 * How much of the image has to stay inside the frame, per axis.
 *
 * The frame is free to hang off the image, but not to leave it altogether: a crop with no
 * image in it saves a fully transparent PNG, which uploads happily and then renders as
 * nothing at all. Restored by moving the image, because the frame is what the admin is
 * currently pointing at.
 */
const MIN_OVERLAP_PX = 24;

/**
 * Breathing room between the fitted image and the edge of the stage.
 *
 * The corner handles are drawn centred on the frame's corners, so without this the ones on
 * whichever axis the fit is bound by would sit half outside the stage and be clipped. The
 * padding buys that room *without* the frame having to start inset from the image -- which
 * matters more than it looks: the frame opens on the whole image, so opening the editor and
 * saving without touching anything returns exactly what went in. An inset here would instead
 * shave a border off on every visit, and an admin re-framing their logo twice would quietly
 * erode it.
 */
const STAGE_PADDING_PX = 12;

const clamp = (value: number, low: number, high: number): number =>
    Math.min(Math.max(value, low), high);

/** The scale at which the whole image just fits the padded stage. `zoom` multiplies this. */
export const fitScale = (state: CropState): number =>
    Math.min(
        (state.stage.width - STAGE_PADDING_PX * 2) / state.natural.width,
        (state.stage.height - STAGE_PADDING_PX * 2) / state.natural.height
    );

const scaleOf = (state: CropState): number => fitScale(state) * state.zoom;

/**
 * The image's rectangle on the stage at the current zoom and offset.
 *
 * Exported because the dialog has to draw the image at exactly this rectangle. It used to
 * work its own out, from the *unpadded* stage, and so drew the image about 8% larger than
 * every number in here believed -- leaving a sliver of unshaded image outside a frame that
 * claimed to hold all of it, and quietly shaving that sliver off on save.
 */
export const drawnRect = (state: CropState): Rect => {
    const scale: number = scaleOf(state);

    return {
        x: state.offset.x,
        y: state.offset.y,
        width: state.natural.width * scale,
        height: state.natural.height * scale,
    };
};

/**
 * The box the frame lives in: the stage, less the padding the handles need.
 *
 * The frame is bounded by *this* rather than by the image, which is the whole of what lets a
 * crop add margin. At zoom 1 the fitted image fills this box on its binding axis, so nothing
 * about the fit case changes; below zoom 1 the difference between the two is the margin.
 */
const stageBounds = (stage: Size): Rect => ({
    x: STAGE_PADDING_PX,
    y: STAGE_PADDING_PX,
    width: stage.width - STAGE_PADDING_PX * 2,
    height: stage.height - STAGE_PADDING_PX * 2,
});

/**
 * The invariant every mutation restores: the frame and the image still overlap.
 *
 * Deliberately far weaker than "the frame never leaves the image", which is what this used to
 * enforce. Area the image does not cover comes out transparent, and transparent margin is now
 * something an admin can ask for -- so the only thing left worth forbidding is a frame with
 * no image in it at all.
 */
const clampOffset = (state: CropState): CropState => {
    const drawn: Rect = drawnRect(state);

    const overlapX: number = Math.min(MIN_OVERLAP_PX, drawn.width, state.frame.width);
    const overlapY: number = Math.min(MIN_OVERLAP_PX, drawn.height, state.frame.height);

    return {
        ...state,
        offset: {
            x: clamp(
                state.offset.x,
                state.frame.x + overlapX - drawn.width,
                state.frame.x + state.frame.width - overlapX
            ),
            y: clamp(
                state.offset.y,
                state.frame.y + overlapY - drawn.height,
                state.frame.y + state.frame.height - overlapY
            ),
        },
    };
};

/**
 * The whole image, centred, framed edge to edge.
 *
 * With an `aspect`, the frame instead opens as the largest rectangle of that shape that fits
 * *inside* the image, centred on it. Edge to edge would otherwise mean a frame hanging off
 * two sides of a photograph that is not already square, so the first thing somebody saw of
 * their own face would be two bands of white -- the opposite of the "opening the editor and
 * saving returns what went in" property this function is written around.
 */
export const initialCrop = (natural: Size, stage: Size, aspect?: number): CropState => {
    const base: CropState = {
        natural,
        stage,
        zoom: 1,
        offset: { x: 0, y: 0 },
        frame: { x: 0, y: 0, width: 0, height: 0 },
        aspect,
    };

    const scale: number = fitScale(base);
    const width: number = natural.width * scale;
    const height: number = natural.height * scale;
    const x: number = (stage.width - width) / 2;
    const y: number = (stage.height - height) / 2;

    if (!aspect) {
        return { ...base, offset: { x, y }, frame: { x, y, width, height } };
    }

    const frameWidth: number = Math.min(width, height * aspect);
    const frameHeight: number = frameWidth / aspect;

    return {
        ...base,
        offset: { x, y },
        frame: {
            x: x + (width - frameWidth) / 2,
            y: y + (height - frameHeight) / 2,
            width: frameWidth,
            height: frameHeight,
        },
    };
};

/** Moves the image under a stationary frame. */
export const panBy = (state: CropState, dx: number, dy: number): CropState =>
    clampOffset({ ...state, offset: { x: state.offset.x + dx, y: state.offset.y + dy } });

/**
 * Zooms about the centre of the *frame*, not of the stage.
 *
 * The frame is what the admin is looking at, so it is what should stay put; zooming about
 * the stage centre slides their subject out from under the crop and they have to chase it.
 * It is also what makes margin feel like a control rather than an accident: park the frame
 * where the logo should sit, zoom out, and the space grows evenly around it.
 */
export const zoomTo = (state: CropState, zoom: number): CropState => {
    const next: number = clamp(zoom, MIN_ZOOM, MAX_ZOOM);
    const before: number = scaleOf(state);
    const after: number = fitScale(state) * next;

    const centreX: number = state.frame.x + state.frame.width / 2;
    const centreY: number = state.frame.y + state.frame.height / 2;

    // Where that centre sits in the image's own pixels, which is what must not move.
    const imageX: number = (centreX - state.offset.x) / before;
    const imageY: number = (centreY - state.offset.y) / before;

    // The frame keeps its size. Zooming out until the image is smaller than it is precisely
    // how an admin asks for margin, so there is nothing here left to correct.
    return clampOffset({
        ...state,
        zoom: next,
        offset: { x: centreX - imageX * after, y: centreY - imageY * after },
    });
};

/**
 * Drags one corner. Free aspect unless `state.aspect` locks it.
 *
 * Free is the logo's case: the two axes are independent, so a wordmark can be framed as the
 * wide strip it is and a round mark as a square, without either being letterboxed. Locked is
 * the avatar's, where the shape is not the admin's to choose because the result is drawn in
 * a circle.
 *
 * Bounded by the stage either way, so a corner may be pulled out past the image to leave
 * space beside the mark. Pulling one far enough that the image would fall out of the frame
 * altogether drags the image along instead, via `clampOffset`.
 */
export const resizeFrameBy = (
    state: CropState,
    corner: Corner,
    dx: number,
    dy: number
): CropState => {
    const bounds: Rect = stageBounds(state.stage);
    const movesLeft: boolean = corner === 'nw' || corner === 'sw';
    const movesTop: boolean = corner === 'nw' || corner === 'ne';

    // The edges the drag does not touch stay exactly where they are.
    const fixedRight: number = state.frame.x + state.frame.width;
    const fixedBottom: number = state.frame.y + state.frame.height;

    if (state.aspect) {
        return clampOffset({
            ...state,
            frame: lockedFrame(state, bounds, movesLeft, movesTop, dx, dy),
        });
    }

    let left: number = movesLeft ? state.frame.x + dx : state.frame.x;
    let top: number = movesTop ? state.frame.y + dy : state.frame.y;
    let right: number = movesLeft ? fixedRight : fixedRight + dx;
    let bottom: number = movesTop ? fixedBottom : fixedBottom + dy;

    if (movesLeft) {
        left = clamp(left, bounds.x, fixedRight - MIN_FRAME_PX);
    } else {
        right = clamp(right, state.frame.x + MIN_FRAME_PX, bounds.x + bounds.width);
    }

    if (movesTop) {
        top = clamp(top, bounds.y, fixedBottom - MIN_FRAME_PX);
    } else {
        bottom = clamp(bottom, state.frame.y + MIN_FRAME_PX, bounds.y + bounds.height);
    }

    return clampOffset({
        ...state,
        frame: { x: left, y: top, width: right - left, height: bottom - top },
    });
};

/**
 * One corner drag, reduced to a single number: the frame's new width.
 *
 * A locked frame has one degree of freedom, so a drag that moves both axes has to be resolved
 * into one. `Math.max` of the two candidates is what makes it feel like dragging rather than
 * fighting -- the frame follows whichever direction the pointer actually went, instead of
 * being pinned by the axis that happened to move less.
 *
 * Both bounds are then expressed in width before clamping, which is the part that is easy to
 * get wrong: clamping x and y separately and *then* re-deriving the other axis lets the frame
 * escape the stage on whichever side was not the one clamped.
 */
const lockedFrame = (
    state: CropState,
    bounds: Rect,
    movesLeft: boolean,
    movesTop: boolean,
    dx: number,
    dy: number
): Rect => {
    const aspect: number = state.aspect as number;

    const fixedRight: number = state.frame.x + state.frame.width;
    const fixedBottom: number = state.frame.y + state.frame.height;

    const wanted: number = Math.max(
        state.frame.width + (movesLeft ? -dx : dx),
        (state.frame.height + (movesTop ? -dy : dy)) * aspect
    );

    // How far the frame may grow from its fixed corner, on each axis, in width terms.
    const roomX: number = movesLeft ? fixedRight - bounds.x : bounds.x + bounds.width - state.frame.x;
    const roomY: number = movesTop ? fixedBottom - bounds.y : bounds.y + bounds.height - state.frame.y;

    const smallest: number = Math.max(MIN_FRAME_PX, MIN_FRAME_PX * aspect);
    const largest: number = Math.min(roomX, roomY * aspect);

    // A stage too small to hold even the minimum takes the largest that fits, rather than
    // letting the floor push the frame back out through the edge it was clamped against.
    const width: number = largest < smallest ? largest : clamp(wanted, smallest, largest);
    const height: number = width / aspect;

    return {
        x: movesLeft ? fixedRight - width : state.frame.x,
        y: movesTop ? fixedBottom - height : state.frame.y,
        width,
        height,
    };
};

/**
 * The frame expressed in the source image's own pixels -- what the canvas is set up around.
 *
 * Deliberately unclamped. `x` and `y` go negative and the width and height run past the
 * image's own once the frame hangs off it, and every one of those cases is a margin the
 * admin asked for; clamping them back would be silently refusing the request.
 */
export const sourceRect = (state: CropState): Rect => {
    const scale: number = scaleOf(state);

    return {
        x: (state.frame.x - state.offset.x) / scale,
        y: (state.frame.y - state.offset.y) / scale,
        width: state.frame.width / scale,
        height: state.frame.height / scale,
    };
};

/**
 * What the crop is written out at.
 *
 * The `min(1, ...)` is load-bearing: **never upscale**. Cropping a 90x30 mark out of a small
 * PNG gives back 90x30, not a soft 512x171 that only looks like more detail.
 *
 * Margin counts toward that long edge like anything else, so padding is spent out of the
 * mark's own resolution -- four times the width means a quarter of the detail. That is the
 * honest arithmetic of a bounded output, and the reason `MIN_ZOOM` stops where it does.
 */
export const outputSize = (source: Rect, maxEdge: number = MAX_LOGO_EDGE): Size => {
    const scale: number = Math.min(1, maxEdge / Math.max(source.width, source.height));

    return {
        width: Math.max(1, Math.round(source.width * scale)),
        height: Math.max(1, Math.round(source.height * scale)),
    };
};

/**
 * The zoom slider's track is logarithmic, and runs 0 to 1 rather than in zoom.
 *
 * A linear track from 0.25 to 8 puts fit -- the value an admin returns to most -- a tenth of
 * the way along, with the whole of the zoom-out range crammed against the left stop. A
 * constant ratio per pixel instead gives both ends the same feel, and lands fit at
 * `FIT_SLIDER`.
 */
export const sliderToZoom = (position: number): number =>
    MIN_ZOOM * Math.pow(MAX_ZOOM / MIN_ZOOM, clamp(position, 0, 1));

export const zoomToSlider = (zoom: number): number =>
    Math.log(clamp(zoom, MIN_ZOOM, MAX_ZOOM) / MIN_ZOOM) / Math.log(MAX_ZOOM / MIN_ZOOM);

/** Where fit sits on that track, for the slider's one tick mark. */
export const FIT_SLIDER: number = zoomToSlider(1);

/**
 * What a crop is encoded as, which is a property of the surface it is for rather than of the
 * cropping.
 *
 * They divide on transparency, for reasons that live on the server and in the browser: a logo
 * must keep a transparent background and survive an email client, so it is PNG at 512px, and a
 * favicon must sit on a tab of any colour, so it is PNG at 256px. A hero is an opaque
 * photograph across the top of a page and is never emailed, so it is JPEG at 1920px, where PNG
 * would be several megabytes to no visible end.
 */
export interface CropEncoding {
    maxEdge: number;
    type: 'image/png' | 'image/jpeg';
    /** JPEG only; ignored for PNG, which is lossless. */
    quality?: number;
    extension: string;
}

export const LOGO_ENCODING: CropEncoding = {
    maxEdge: MAX_LOGO_EDGE,
    type: 'image/png',
    extension: 'png',
};

/**
 * 0.82 is the usual knee of the quality curve for photographic content: artefacts are not
 * visible at arm's length and the file is a fraction of what 0.95 costs.
 */
export const HERO_ENCODING: CropEncoding = {
    maxEdge: MAX_HERO_EDGE,
    type: 'image/jpeg',
    quality: 0.82,
    extension: 'jpg',
};

/**
 * PNG like the logo and for a sharper version of the same reason: a browser tab is whatever
 * colour the reader's theme makes it, and JPEG's missing alpha channel would put a white
 * rectangle around the mark on every dark-themed browser in the world.
 *
 * The clinic's mark cropped square rather than the logo re-used, which is the whole point of
 * the favicon existing: a wide wordmark scaled into a 16px box is an illegible sliver.
 */
export const FAVICON_ENCODING: CropEncoding = {
    maxEdge: MAX_FAVICON_EDGE,
    type: 'image/png',
    extension: 'png',
};

/**
 * A photograph, like the hero, so JPEG -- but at 0.85 rather than 0.82.
 *
 * A face at 512px is a much smaller canvas than a 1920px hero, so the few kilobytes the extra
 * quality costs are worth not having artefacts around somebody's eyes and hairline, which is
 * exactly where a face shows them and a landscape does not.
 *
 * PNG would be the wrong tool for the same reason it is wrong for the hero: no transparency
 * is wanted (the result is masked into a circle by CSS, not by an alpha channel) and a
 * photographic 512px PNG is several times the size to no visible end.
 */
export const AVATAR_ENCODING: CropEncoding = {
    maxEdge: MAX_AVATAR_EDGE,
    type: 'image/jpeg',
    quality: 0.85,
    extension: 'jpg',
};

/** The bytes are what they are now whatever they were before, so the name should not lie. */
const renameTo = (name: string, extension: string): string =>
    `${name.replace(/\.[^.]+$/, '')}.${extension}`;

/**
 * Renders the crop to a file in the requested encoding, or `null` if it could not be produced.
 *
 * **Null rather than the original file**, which is where this parts company with
 * `downscaleImage`. That one falls back to the original because the server takes it either
 * way; here the original may well be the JPEG the admin picked, and the logo route is
 * PNG-only, so falling back would turn a canvas failure into a 400 about the wrong thing.
 * The dialog reports it and stays open instead.
 *
 * No size check against the server's cap, for either encoding: a 512-bounded PNG cannot exceed
 * ~1.05MB against a 2MB cap, and a 1920-bounded JPEG at quality 0.82 does not approach 4MB, so
 * the branch would be unreachable in both cases.
 */
export const cropImage = async (
    file: File,
    state: CropState,
    encoding: CropEncoding = LOGO_ENCODING
): Promise<File | null> => {
    try {
        const image: HTMLImageElement = await loadImage(file);
        const source: Rect = sourceRect(state);
        const out: Size = outputSize(source, encoding.maxEdge);

        const canvas: HTMLCanvasElement = document.createElement('canvas');

        canvas.width = out.width;
        canvas.height = out.height;

        const context: CanvasRenderingContext2D | null = canvas.getContext('2d');

        if (!context) {
            return null;
        }

        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';

        // Never fill the canvas when the output is a PNG. It starts fully transparent, so the
        // source's alpha comes through untouched, and any margin the admin framed past the
        // edge of the picture stays empty rather than turning up white -- and a JPEG *source*,
        // which has no alpha, produces the opaque box the dialog has already shown the admin
        // against the real header.
        //
        // A JPEG *output* is the one case that must fill, and white rather than anything else:
        // JPEG has no alpha at all, and an unfilled canvas encodes its transparent pixels as
        // black. That covers both a source that had transparency and the margin a hero's frame
        // can now be dragged out into, and in both cases black would be a surprise where white
        // is the page.
        if (encoding.type === 'image/jpeg') {
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, out.width, out.height);
        }

        // The whole image, placed and scaled so that the frame lands on the canvas, rather
        // than a source rectangle read out of it. The same picture whenever the frame is
        // inside the image -- and the only one of the two that is defined when it is not,
        // since a source rectangle straddling the edge is a case browsers have never agreed
        // on. Here the canvas simply clips, and what the image does not reach is left as
        // whatever the fill above did or did not put there.
        const scaleX: number = out.width / source.width;
        const scaleY: number = out.height / source.height;

        context.drawImage(
            image,
            -source.x * scaleX,
            -source.y * scaleY,
            image.naturalWidth * scaleX,
            image.naturalHeight * scaleY
        );

        const blob: Blob | null = await new Promise((resolve) =>
            canvas.toBlob(resolve, encoding.type, encoding.quality)
        );

        return blob
            ? new File([blob], renameTo(file.name, encoding.extension), { type: encoding.type })
            : null;
    } catch {
        return null;
    }
};
